import type { ConceptId, SpellingPart, Word, WordMapping } from '../../types/content'
import { conceptById, mappingAliasToId, mappingById, mappingSeedById } from './concepts'

interface WordGroup {
  conceptId: ConceptId
  difficulty: 1 | 2 | 3 | 4 | 5
  rows: string
  pseudo?: boolean
  teacherSupported?: boolean
}

const group = (conceptId: ConceptId, difficulty: WordGroup['difficulty'], rows: string, options: Pick<WordGroup, 'pseudo' | 'teacherSupported'> = {}): WordGroup => ({
  conceptId, difficulty, rows, ...options,
})

const groups: WordGroup[] = [
  group('gpc_n_n', 1, `
at|a,t|在
sat|s,a,t|坐（过去式）
pat|p,a,t|轻拍
tap|t,a,p|轻敲
sit|s,i,t|坐
tip|t,i,p|尖端
pit|p,i,t|小坑
pin|p,i,n|别针
tin|t,i,n|锡罐
nap|n,a,p|小睡
pan|p,a,n|平底锅`),

  group('gpc_k_k', 1, `
mat|m,a,t|垫子
map|m,a,p|地图
man|m,a,n|男人
mad|m,a,d|生气的
dam|d,a,m|水坝
dad|d,a,d|爸爸
did|d,i,d|做了
dim|d,i,m|昏暗的
dip|d,i,p|蘸一下
dog|d,o,g|狗
dig|d,i,g|挖
dot|d,o,t|小点
got|g,o,t|得到
gap|g,a,p|空隙
gas|g,a,s|气体
cot|c_k,o,t|小床
cat|c_k,a,t|猫
can|c_k,a,n|罐子
cap|c_k,a,p|帽子
cop|c_k,o,p|警察
kid|k,i,d|孩子
kit|k,i,t|工具包
kin|k,i,n|亲属
top|t,o,p|顶部
pot|p,o,t|锅
pop|p,o,p|砰的一声
not|n,o,t|不是
nod|n,o,d|点头
mop|m,o,p|拖把
mom|m,o,m|妈妈
in|i,n|在里面
on|o,n|在上面`),

  group('gpc_l_l', 1, `
bed|b,e,d|床
red|r,e,d|红色
hen|h,e,n|母鸡
pen|p,e,n|钢笔
men|m,e,n|男人们
ten|t,e,n|十
net|n,e,t|网
pet|p,e,t|宠物
get|g,e,t|得到
den|d,e,n|洞穴
run|r,u,n|跑
sun|s,u,n|太阳
rub|r,u,b|擦
hut|h,u,t|小屋
mud|m,u,d|泥
cup|c_k,u,p|杯子
pup|p,u,p|小狗
bug|b,u,g|小虫
gum|g,u,m|口香糖
nut|n,u,t|坚果
bun|b,u,n|小圆面包
cut|c_k,u,t|切
bat|b,a,t|球棒
big|b,i,g|大的
fun|f,u,n|有趣
fan|f,a,n|风扇
lip|l,i,p|嘴唇
leg|l,e,g|腿
log|l,o,g|木头
bag|b,a,g|包
bin|b,i,n|箱子
ham|h,a,m|火腿
him|h,i,m|他
hit|h,i,t|击打
hot|h,o,t|热的
hop|h,o,p|跳
rag|r,a,g|抹布
ram|r,a,m|公羊
rat|r,a,t|老鼠
rot|r,o,t|腐烂
rip|r,i,p|撕开
lap|l,a,p|膝上
lit|l,i,t|点亮了
lot|l,o,t|许多
fig|f,i,g|无花果
fin|f,i,n|鱼鳍
fog|f,o,g|雾
fit|f,i,t|合适
fed|f,e,d|喂了
led|l,e,d|带领了
lid|l,i,d|盖子
lug|l,u,g|用力拖
hug|h,u,g|拥抱
hum|h,u,m|哼唱
rug|r,u,g|地毯
rid|r,i,d|去除
rob|r,o,b|抢走
rib|r,i,b|肋骨
cab|c_k,a,b|出租车
had|h,a,d|有过`),

  group('gpc_qu_kw', 2, `
jam|j,a,m|果酱
jet|j,e,t|喷气飞机
jog|j,o,g|慢跑
jug|j,u,g|水壶
van|v,a,n|面包车
vet|v,e,t|兽医
vat|v,a,t|大桶
win|w,i,n|赢
wet|w,e,t|湿的
wig|w,i,g|假发
wax|w,a,x_ks|蜡
fox|f,o,x_ks|狐狸
box|b,o,x_ks|盒子
zip|z,i,p|拉链
zap|z,a,p|快速击中
yes|y_j,e,s|是
yam|y_j,a,m|山药
yak|y_j,a,k|牦牛
yum|y_j,u,m|好吃
quiz|qu_kw,i,z|小测验
quit|qu_kw,i,t|停止
quip|qu_kw,i,p|俏皮话
fix|f,i,x_ks|修理
mix|m,i,x_ks|混合
six|s,i,x_ks|六
ox|o,x_ks|公牛
max|m,a,x_ks|最大
vex|v,e,x_ks|烦扰`),

  group('gpc_tt_t', 2, `
back|b,a,ck|背部
duck|d,u,ck|鸭子
neck|n,e,ck|脖子
pick|p,i,ck|挑选
rock|r,o,ck|石头
sack|s,a,ck|袋子
sick|s,i,ck|生病的
lock|l,o,ck|锁
pack|p,a,ck|打包
tick|t,i,ck|滴答声
off|o,ff|关闭
puff|p,u,ff|一小团
huff|h,u,ff|呼气
cuff|c_k,u,ff|袖口
bell|b,e,ll|铃铛
hill|h,i,ll|小山
doll|d,o,ll|玩偶
fell|f,e,ll|落下了
well|w,e,ll|很好
mill|m,i,ll|磨坊
miss|m,i,ss|想念
fuss|f,u,ss|小题大做
kiss|k,i,ss|亲吻
mess|m,e,ss|乱糟糟
boss|b,o,ss|负责人
less|l,e,ss|更少
buzz|b,u,zz|嗡嗡声
fizz|f,i,zz|嘶嘶冒泡
jazz|j,a,zz|爵士乐
egg|e,gg|鸡蛋`),

  group('gpc_wh_w', 2, `
ship|sh,i,p|船
shop|sh,o,p|商店
fish|f,i,sh|鱼
dish|d,i,sh|盘子
shut|sh,u,t|关上
shell|sh,e,ll|贝壳
shed|sh,e,d|小棚
wish|w,i,sh|愿望
mash|m,a,sh|捣碎
rush|r,u,sh|冲
chat|ch,a,t|聊天
chin|ch,i,n|下巴
much|m,u,ch|许多
chop|ch,o,p|切碎
chip|ch,i,p|小片
rich|r,i,ch|富有的
lunch|l,u,n,ch|午餐
bench|b,e,n,ch|长凳
chest|ch,e,s,t|箱子
thin|th_unvoiced,i,n|薄的
bath|b,a,th_unvoiced|洗澡
moth|m,o,th_unvoiced|飞蛾
thick|th_unvoiced,i,ck|厚的
thud|th_unvoiced,u,d|砰的一声
this|th_voiced,i,s|这个
that|th_voiced,a,t|那个
them|th_voiced,e,m|他们
then|th_voiced,e,n|然后
with|w,i,th_voiced|和
ring|r,i,ng|戒指
song|s,o,ng|歌曲
wing|w,i,ng|翅膀
king|k,i,ng|国王
long|l,o,ng|长的
sing|s,i,ng|唱歌
hang|h,a,ng|悬挂
pink|p,i,nk|粉色
bank|b,a,nk|河岸
sink|s,i,nk|水槽
link|l,i,nk|连接
junk|j,u,nk|旧杂物
wink|w,i,nk|眨眼
when|wh,e,n|什么时候
whip|wh,i,p|快速挥动
whim|wh,i,m|一时想法
whiz|wh,i,z|嗖地飞过`),

  group('blend_ccvc_cvcc', 2, `
clap|c_k,l,a,p|拍手
clip|c_k,l,i,p|夹子
clam|c_k,l,a,m|蛤蜊
club|c_k,l,u,b|俱乐部
flap|f,l,a,p|拍动
flat|f,l,a,t|平的
flip|f,l,i,p|翻转
flag|f,l,a,g|旗子
glad|g,l,a,d|高兴的
glum|g,l,u,m|闷闷不乐
plan|p,l,a,n|计划
plot|p,l,o,t|小块地
plug|p,l,u,g|插头
slip|s,l,i,p|滑倒
sled|s,l,e,d|雪橇
slim|s,l,i,m|苗条的
crab|c_k,r,a,b|螃蟹
crib|c_k,r,i,b|婴儿床
crop|c_k,r,o,p|作物
drum|d,r,u,m|鼓
drag|d,r,a,g|拖
drip|d,r,i,p|滴落
frog|f,r,o,g|青蛙
grin|g,r,i,n|咧嘴笑
grab|g,r,a,b|抓住
prop|p,r,o,p|支撑物
trip|t,r,i,p|旅行
trim|t,r,i,m|修剪
trap|t,r,a,p|陷阱
scan|s,c_k,a,n|扫视
skip|s,k,i,p|跳过
skin|s,k,i,n|皮肤
skid|s,k,i,d|打滑
smell|s,m,e,ll|气味
smug|s,m,u,g|自满的
snap|s,n,a,p|啪地断开
snip|s,n,i,p|剪一下
spin|s,p,i,n|旋转
spot|s,p,o,t|斑点
step|s,t,e,p|脚步
stop|s,t,o,p|停止
swim|s,w,i,m|游泳
swam|s,w,a,m|游泳了
stem|s,t,e,m|茎
hand|h,a,n,d|手
sand|s,a,n,d|沙子
pond|p,o,n,d|池塘
tent|t,e,n,t|帐篷
bent|b,e,n,t|弯曲的
camp|c_k,a,m,p|营地
lamp|l,a,m,p|灯
lift|l,i,f,t|举起
soft|s,o,f,t|柔软的
milk|m,i,l,k|牛奶
help|h,e,l,p|帮助
belt|b,e,l,t|腰带
desk|d,e,s,k|书桌
mask|m,a,s,k|面具
nest|n,e,s,t|鸟巢
fast|f,a,s,t|快的
best|b,e,s,t|最好的
gift|g,i,f,t|礼物`),

  group('blend_ccvc_cvcc', 2, `
lat|l,a,t|小怪词
fim|f,i,m|小怪词
sot|s,o,t|小怪词
nup|n,u,p|小怪词
teg|t,e,g|小怪词`, { pseudo: true }),

  group('gpc_u_e_long_u', 3, `
cake|c_k,a_e,k|蛋糕
make|m,a_e,k|制作
name|n,a_e,m|名字
game|g,a_e,m|游戏
same|s,a_e,m|相同的
late|l,a_e,t|晚的
gate|g,a_e,t|大门
safe|s,a_e,f|安全的
wave|w,a_e,v|挥手
bike|b,i_e,k|自行车
time|t,i_e,m|时间
five|f,i_e,v|五
ride|r,i_e,d|骑行
hide|h,i_e,d|躲藏
kite|k,i_e,t|风筝
home|h,o_e,m|家
hope|h,o_e,p|希望
note|n,o_e,t|便条
rope|r,o_e,p|绳子
nose|n,o_e,s_z|鼻子
cube|c_k,u_e_yoo,b|立方体
cute|c_k,u_e_yoo,t|可爱的
mule|m,u_e_yoo,l|骡子
rule|r,u_e_long_u,l|规则
flute|f,l,u_e_long_u,t|长笛`),

  group('gpc_ew_yoo', 3, `
rain|r,ai,n|雨
train|t,r,ai,n|火车
tail|t,ai,l|尾巴
mail|m,ai,l|邮件
paint|p,ai,n,t|绘画
day|d,ay|白天
play|p,l,ay|玩
stay|s,t,ay|停留
tray|t,r,ay|托盘
may|m,ay|可能
see|s,ee|看见
green|g,r,ee,n|绿色
seed|s,ee,d|种子
feet|f,ee,t|脚
sleep|s,l,ee,p|睡觉
team|t,ea_long_e,m|团队
read|r,ea_long_e,d|阅读
leaf|l,ea_long_e,f|叶子
beach|b,ea_long_e,ch|海滩
clean|c_k,l,ea_long_e,n|干净的
night|n,igh,t|夜晚
light|l,igh,t|光
right|r,igh,t|右边
high|h,igh|高的
pie|p,ie|馅饼
tie|t,ie|领带
my|m,y_long_i|我的
fly|f,l,y_long_i|飞
sky|s,k,y_long_i|天空
boat|b,oa,t|船
road|r,oa,d|道路
goat|g,oa,t|山羊
coat|c_k,oa,t|外套
snow|s,n,ow_long_o|雪
grow|g,r,ow_long_o|生长
show|sh,ow_long_o|展示
blue|b,l,ue|蓝色
true|t,r,ue|真的
glue|g,l,ue|胶水
grew|g,r,ew_long_u|长大了
new|n,ew_long_u|新的
few|f,ew_yoo|少量
dew|d,ew_yoo|露水`),

  group('gpc_oo_short', 3, `
moon|m,oo_long,n|月亮
food|f,oo_long,d|食物
room|r,oo_long,m|房间
zoo|z,oo_long|动物园
boot|b,oo_long,t|靴子
book|b,oo_short,k|书
look|l,oo_short,k|看
foot|f,oo_short,t|脚
wood|w,oo_short,d|木头
cook|c_k,oo_short,k|做饭`),

  group('gpc_ur_r', 3, `
car|c_k,ar|汽车
star|s,t,ar|星星
farm|f,ar,m|农场
park|p,ar,k|公园
yard|y_j,ar,d|院子
for|f,or|为了
fork|f,or,k|叉子
storm|s,t,or,m|暴风雨
corn|c_k,or,n|玉米
horn|h,or,n|角
her|h,er_r|她
term|t,er_r,m|学期
bird|b,ir,d|鸟
girl|g,ir,l|女孩
shirt|sh,ir,t|衬衫
turn|t,ur,n|转弯
burn|b,ur,n|燃烧
curl|c_k,ur,l|卷曲`),

  group('gpc_oy_oy', 3, `
out|ou,t|外面
cloud|c_k,l,ou,d|云
sound|s,ou,n,d|声音
round|r,ou,n,d|圆的
cow|c_k,ow_ow|奶牛
town|t,ow_ow,n|城镇
owl|ow_ow,l|猫头鹰
coin|c_k,oi,n|硬币
soil|s,oi,l|土壤
join|j,oi,n|加入
point|p,oi,n,t|指向
boy|b,oy|男孩
toy|t,oy|玩具
joy|j,oy|快乐`),

  group('gpc_eer_r', 4, `
saw|s,aw|看见了
paw|p,aw|爪子
draw|d,r,aw|画画
straw|s,t,r,aw|吸管
haul|h,au,l|拖运
air|air|空气
chair|ch,air|椅子
fair|f,air|公平的
care|c_k,are|关心
share|sh,are|分享
ear|ear|耳朵
hear|h,ear|听见
near|n,ear|附近
deer|d,eer|鹿
cheer|ch,eer|欢呼`),

  group('gpc_mb_m', 4, `
phone|ph,o_e,n|电话
graph|g,r,a,ph|图表
match|m,a,tch|比赛
catch|c_k,a,tch|接住
patch|p,a,tch|补丁
bridge|b,r,i,dge|桥
badge|b,a,dge|徽章
edge|e,dge|边缘
cent|c_soft,e,n,t|美分
gem|g_soft,e,m|宝石
knee|kn,ee|膝盖
knock|kn,o,ck|敲门
write|wr,i_e,t|写
wrist|wr,i,s,t|手腕
lamb|l,a,mb|小羊
thumb|th_unvoiced,u,mb|拇指`),

  group('context_s_ea', 4, `
gym|g,y_short_i,m|体育馆
myth|m,y_short_i,th_unvoiced|神话
happy|h,a,pp,y_long_e|开心的
sunny|s,u,nn,y_long_e|晴朗的
funny|f,u,nn,y_long_e|有趣的
is|i,s_z|是
his|h,i,s_z|他的
head|h,ea_short_e,d|头
bread|b,r,ea_short_e,d|面包
city|c_soft,i,t,y_long_e|城市
go|g,o_open_long_o|去
want|w,a_after_w,n,t|想要
open|o_open_long_o,p,e,n|打开
robot|r,o_open_long_o,b,o,t|机器人
tiger|t,i_long_i,g,er_unstressed|老虎`),

  group('morph_est', 5, `
cats|c_k,a,t,suffix_s_s|猫（复数）
cups|c_k,u,p,suffix_s_s|杯子（复数）
dogs|d,o,g,suffix_s_z|狗（复数）
pans|p,a,n,suffix_s_z|锅（复数）
buses|b,u,s,suffix_es_iz|公交车（复数）
boxes|b,o,x_ks,suffix_es_iz|盒子（复数）
jumped|j,u,m,p,suffix_ed_t|跳了
helped|h,e,l,p,suffix_ed_t|帮助了
played|p,l,ay,suffix_ed_d|玩了
wanted|w,a_after_w,n,t,suffix_ed_id|想要了
lifted|l,i,f,t,suffix_ed_id|举起了
helping|h,e,l,p,suffix_ing|正在帮助
jumping|j,u,m,p,suffix_ing|正在跳
faster|f,a,s,t,suffix_er|更快
helper|h,e,l,p,suffix_er|帮手
fastest|f,a,s,t,suffix_est|最快的
softest|s,o,f,t,suffix_est|最柔软的`),

  group('syllable_r_controlled', 5, `
about|a_schwa,b,ou,t|关于
sofa|s,o_open_long_o,f,a_schwa|沙发
lemon|l,e,m,o_schwa,n|柠檬
album|a,l,b,u_schwa,m|相册
sunset|s,u,n,s,e,t|日落
napkin|n,a,p,k,i,n|餐巾
inside|i,n,s,i_e,d|里面
sunshine|s,u,n,sh,i_e,n|阳光
raincoat|r,ai,n,c_k,oa,t|雨衣
playground|p,l,ay,g,r,ou,n,d|游乐场
farmyard|f,ar,m,y_j,ar,d|农家庭院
starfish|s,t,ar,f,i,sh|海星`),

  group('schwa_unstressed', 5, `
taken|t,a_open_long_a,k,e_schwa,n|拿走了
pencil|p,e,n,c_soft,i_schwa,l|铅笔
august|au,g,u_schwa,s,t|八月
apron|a_open_long_a,p,r,o,n|围裙
giant|g_soft,i_long_i,a_schwa,n,t|巨大的
rabbit|r,a,bb,i,t|兔子
hobby|h,o,bb,y_long_e|爱好
hidden|h,i,dd,e,n|隐藏的
summer|s,u,mm,er_unstressed|夏天
hammer|h,a,mm,er_unstressed|锤子
kitten|k,i,tt,e,n|小猫
letter|l,e,tt,er_unstressed|字母
carrot|c_k,a,rr,o,t|胡萝卜
parrot|p,a,rr,o,t|鹦鹉
puppet|p,u,pp,e,t|木偶`),
]

const meaningBySpelling = new Map<string, string>()

const parseRows = (rows: string) => rows.trim().split('\n').map((row) => {
  const [spelling, aliasText, meaningZh = '可拼读词'] = row.trim().split('|')
  if (!spelling || !aliasText) throw new Error(`Invalid word row: ${row}`)
  meaningBySpelling.set(spelling, meaningZh)
  return { spelling, aliases: aliasText.split(',') }
})

const claim = (spelling: string, grapheme: string, claimed: boolean[]): SpellingPart[] => {
  const chunks = grapheme.split('_')
  const parts: SpellingPart[] = []
  let searchFrom = 0
  for (const chunk of chunks) {
    let found = -1
    for (let index = searchFrom; index <= spelling.length - chunk.length; index += 1) {
      const free = Array.from({ length: chunk.length }, (_, offset) => !claimed[index + offset]).every(Boolean)
      if (free && spelling.slice(index, index + chunk.length) === chunk) {
        found = index
        break
      }
    }
    if (found < 0) {
      for (let index = 0; index <= spelling.length - chunk.length; index += 1) {
        const free = Array.from({ length: chunk.length }, (_, offset) => !claimed[index + offset]).every(Boolean)
        if (free && spelling.slice(index, index + chunk.length) === chunk) {
          found = index
          break
        }
      }
    }
    if (found < 0) throw new Error(`Cannot place grapheme ${grapheme} in ${spelling}`)
    for (let offset = 0; offset < chunk.length; offset += 1) claimed[found + offset] = true
    parts.push({ start: found, length: chunk.length })
    searchFrom = found + chunk.length
  }
  return parts
}

const makeWord = (spelling: string, aliases: string[], source: WordGroup): Word => {
  const claimed = Array.from({ length: spelling.length }, () => false)
  const wordMappings: WordMapping[] = aliases.map((alias) => {
    const mappingId = mappingAliasToId.get(alias)
    if (!mappingId) throw new Error(`Unknown mapping alias ${alias} in ${spelling}`)
    const mapping = mappingById.get(mappingId)
    const seed = mappingSeedById.get(mappingId)
    if (!mapping || !seed) throw new Error(`Missing mapping ${mappingId}`)
    return {
      mappingId,
      graphemeId: mapping.graphemeId,
      grapheme: seed.display,
      phonemeIds: mapping.phonemeIds,
      spellingParts: claim(spelling, seed.display, claimed),
    }
  })
  if (claimed.some((value) => !value)) throw new Error(`Incomplete segmentation for ${spelling}`)
  const mappingConceptIds = wordMappings.map((entry) => mappingById.get(entry.mappingId)?.conceptId).filter((value): value is string => Boolean(value))
  const required = Array.from(new Set(mappingConceptIds)).sort((a, b) => (conceptById.get(a)?.order ?? 0) - (conceptById.get(b)?.order ?? 0))
  return {
    id: `word_${spelling}`,
    spelling,
    phonemeIds: wordMappings.flatMap((entry) => entry.phonemeIds),
    graphemeIds: wordMappings.map((entry) => entry.graphemeId),
    graphemes: wordMappings.map((entry) => entry.grapheme),
    mapping: wordMappings,
    audioId: `word_${spelling}_ga`,
    blendDemoAudioId: source.difficulty <= 3 && !source.pseudo ? `blend_${spelling}_ga` : undefined,
    decodableAfterConcepts: required,
    introducedAtConceptId: source.conceptId,
    difficulty: source.difficulty,
    imageKeyword: source.pseudo ? 'friendly colorful silly word creature, simple children illustration' : `simple children's illustration of ${spelling}, clean white background`,
    meaningZh: meaningBySpelling.get(spelling) ?? '可拼读词',
    isPseudoWord: source.pseudo || undefined,
    teacherSupported: source.teacherSupported || undefined,
  }
}

const builtWords = groups.flatMap((source) => parseRows(source.rows).map(({ spelling, aliases }) => makeWord(spelling, aliases, source)))
const duplicates = builtWords.filter((word, index) => builtWords.findIndex((candidate) => candidate.id === word.id) !== index)
if (duplicates.length) throw new Error(`Duplicate words: ${duplicates.map((word) => word.spelling).join(', ')}`)

export const words: Word[] = builtWords
export const wordById = new Map(words.map((word) => [word.id, word]))
